/**
 * Kopfzeile der Anwendung mit ihren vier Overlays — woertlich nach dem
 * Abschnitt TOPBAR des Entwurfs und den Abschnitten KONTO-MENUE, GLOCKE,
 * WEGWEISER und COMMAND-PALETTE.
 *
 * Enthalten: Navigationsschalter, Logo mit Markenkennung, globale Suche,
 * Vertraulichkeitsmodus, Mandantenumschalter, Erscheinungsbild, aktive
 * Nutzer, Einstellungen (nur Admin), Wegweiser, Glocke, Konto-Menue.
 *
 * Tastenkuerzel wie im Entwurf: Strg+K oeffnet die Suche, `n` den Wegweiser,
 * `?` ebenfalls den Wegweiser, Esc schliesst.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { stil } from './stil';
import { useEntprellt } from '../api/haken';
import { useGlobaleSuche } from '../api/suche';
import { useMeldungen, type Bestandsmeldung, type Meldungsstand } from '../api/meldungen';
import { useDrawer } from './Drawer';
import { KATALOG, symbol } from '../entwurf/katalog';
import {
  KONTO_EINTRAEGE, LOGOBALKEN, LOGOFARBE, MARKEN, THEMES, hausmarkeSvg, segStil,
} from '../entwurf/huelle';
import { routeErlaubt, zielErlaubt, type Profil } from '../entwurf/navigation';
import { DEMO, DEMO_PERSONAS } from '../entwurf/demo';
import { abmelden } from '../anmeldung/msal';
import { datum } from '../entwurf/marker';
import { STICHTAG_TAGE, tagVor, useStand, useStandSetzen } from './Standrahmen';

export interface KopfzeileProps {
  marke: string;
  setzeMarke: (marke: string) => void;
  thema: string;
  setzeThema: (thema: string) => void;
  persona: string;
  setzePersona: (persona: string) => void;
  /**
   * Konten des Entwicklungsbetriebs. Leer, sobald die Anmeldung entscheidet —
   * in einer ausgelieferten Anwendung sucht sich niemand sein Konto aus.
   */
  konten?: { kennung: string; name: string; anwendungsrolle: string; oeCode: string }[] | undefined;
  gewaehltesKonto?: string | undefined;
  setzeGewaehltesKonto?: ((kennung: string) => void) | undefined;
  profil: Profil;
  /** Mandant dieser Instanz — damit fragt die globale Suche ab. */
  mandant: string;
  /** Die echten Rechte aus `/api/ich`; ohne sie wird keine Sammlung gefragt. */
  darfDas: (recht: string | null) => boolean;
  /** Navigation ein- und ausklappen (schmal: Overlay). */
  onNavigation: () => void;
  /** Ab wann die Datenbank einen Verlauf fuehrt — Grenze der Stichtagswahl. */
  verlaufAb?: string | null | undefined;
  /** Angemeldeter Name aus Entra; ohne Anmeldung greift die Persona. */
  name?: string | undefined;
  upn?: string | undefined;
}

/** Anzeigename des Mandanten. Eine Stelle statt drei verstreuter Ternaere. */
function mandantName(marke: string): string {
  return marke === 'howoge' ? 'HOWOGE' : marke === 'dev' ? 'DEV' : 'COMLINE';
}

function initialen(name: string): string {
  return name.split(' ').filter(Boolean).map((w) => w[0] ?? '').join('').slice(0, 2);
}

export function Kopfzeile(p: KopfzeileProps) {
  /**
   * Traegt dieser Mandant eine eigene Wortmarke?
   *
   * Wo ja, steht oben links **nur** sie: kein „IAM“ daneben und keine
   * Mandantenmarke. Der Kunde am 03.09.2026: „da soll kein IAM mehr stehen
   * und da soll auch HOWOGE als Text nicht mehr stehen. Nur noch das Logo.“
   *
   * Er hat recht, und zwar dreifach: das Logo sagt bereits, wessen Haus das
   * ist; „IAM“ stand als Anwendungsname daneben, obwohl der Browserreiter
   * ihn ohnehin fuehrt („HOWOGE - IAM“); und die Mandantenmarke wiederholte
   * nur den Namen aus dem Logo.
   *
   * Fuer COMLINE und DEV bleibt alles, wie es war — dort gibt es keine
   * Wortmarke, und drei namenlose Balken allein sagten nichts. Vor allem
   * bliebe ohne die Mandantenmarke unsichtbar, in welchem Mandanten man
   * handelt, und das ist in einem Rechtewerkzeug der teuerste Fehler.
   */
  const eigeneWortmarke = p.marke === 'howoge';

  const [palette, setzePalette] = useState(false);
  const [wegweiser, setzeWegweiser] = useState(false);
  const [glocke, setzeGlocke] = useState(false);
  const [konto, setzeKonto] = useState(false);
  const [gelesen, setzeGelesen] = useState(false);

  /* Der angemeldete Nutzer — aus `/api/ich`, und sonst nirgendsher.

     Hier stand ein Rueckfall auf `ICH[p.persona]`: fuenf erfundene Personen
     im Quelltext, ausgewaehlt ueber einen Wert aus dem Browserspeicher.
     Faellt `/api/ich` aus, zeigte die Kopfzeile deshalb „Lennart Ahrens"
     statt zu sagen, dass sie den Namen gerade nicht kennt — und das war
     beim Kunden der Dauerzustand, weil `/profil` denselben Weg ging.

     Eine leere Zeichenkette heisst hier „noch nicht bekannt"; die Karte
     unten sagt das aus, statt einen Namen zu erfinden. */
  const name = p.name ?? '';
  const upn = p.upn ?? '';

  /* Zaehler der Glocke — aus dem Bestand.
     Bis zum 18.08.2026 stand hier eine feste „3", danach die Zahl der vier
     erfundenen Meldungen aus `entwurf/huelle.ts`. Beides war eine Behauptung.
     Jetzt zaehlt sie, was wirklich offen ist; solange die Abrufe laufen,
     steht dort **keine Null** — dieselbe Regel wie an den Aufgabenkacheln. */
  const stand = useMeldungen(p.mandant, p.profil, p.darfDas);
  const ungelesen = gelesen ? 0 : stand.meldungen.length;

  // Tastenkuerzel. Sie greifen nicht, waehrend in einem Feld getippt wird.
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      const ziel = e.target as HTMLElement | null;
      const tippt = !!ziel && /^(INPUT|TEXTAREA|SELECT)$/.test(ziel.tagName);
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setzePalette(true); setzeGlocke(false); setzeKonto(false);
        return;
      }
      if (tippt) return;
      if (e.key === 'n' || e.key === '?') {
        e.preventDefault();
        setzeWegweiser(true);
      }
    };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, []);

  const knopf =
    'width:36px;height:36px;display:grid;place-items:center;background:none;border:none;' +
    'border-radius:var(--r-md);color:var(--text-secondary);cursor:pointer';

  return (
    <>
      <header style={stil(
        'position:sticky;top:0;z-index:var(--z-kopfzeile);height:56px;flex:0 0 56px;display:flex;align-items:center;' +
        'gap:12px;padding:0 16px;background:var(--surface-card);border-bottom:1px solid var(--border-subtle)',
      )}>
        <button onClick={p.onNavigation} aria-label="Navigation umschalten"
                className="knopf-still" style={stil(knopf)}>
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Der Verweis traegt seinen Zweck selbst.

            Solange „IAM“ danebenstand, las eine Vorlesehilfe
            „HOWOGE IAM, Link“ — immerhin ein Name. Seit oben links nur
            noch das Logo steht, waere es „HOWOGE, Link“: der Name des
            Hauses, aber kein Hinweis, wohin es geht. */}
        <a href="/" className="kopf-marke" aria-label="Zur Startseite" style={stil(
          'display:flex;align-items:center;gap:10px;text-decoration:none;' +
          'color:var(--text-primary);min-width:0',
        )}>
          {/* Die Marke des Hauses, wo es eine gibt.

              HOWOGE traegt seit dem 03.09.2026 die eigene Wortmarke — der Kunde
              hat es verlangt, und er hat recht: oben links stand unser Zeichen
              in seiner Farbe, was aussieht wie seine Marke und keine ist. Die
              Geometrie steht in `entwurf/huelle.ts`; `hausmarkeSvg` ist ein
              **Nachbau**, kein Originalvektor (Begruendung dort).

              Fuer COMLINE und DEV bleiben die drei Balken. Eine Wortmarke
              nachzubauen, die niemand geschickt hat, waere erfundener Inhalt.

              `dangerouslySetInnerHTML` ist hier vertretbar und sonst nicht:
              die Zeichenkette kommt aus dem eigenen Quelltext, nicht aus dem
              Bestand. Sie an zwei Orten zu fuehren — einmal als JSX hier,
              einmal als Zeichenkette fuer den HTML-Bericht — lief bei den
              Balken schon einmal auseinander. */}
          {eigeneWortmarke ? (
            <span style={stil('display:flex;align-items:center')}
                  dangerouslySetInnerHTML={{ __html: hausmarkeSvg(26) }} />
          ) : (
            <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true">
              {LOGOBALKEN.map(([x, y, b, hh, d]) => (
                <rect key={x} x={x} y={y} width={b} height={hh} rx="2" opacity={d}
                      fill={LOGOFARBE[p.marke] ?? LOGOFARBE['comline']} />
              ))}
            </svg>
          )}
          {!eigeneWortmarke && (
            <span style={stil(
              'font-size:15px;font-weight:600;letter-spacing:.01em;white-space:nowrap',
            )}>IAM</span>
          )}
          {/*
            Umgebungskennzeichnung.

            Vorher ein graues Pillchen in Sekundaerfarbe — nicht zu uebersehen,
            aber auch nicht zu bemerken. „Im falschen Mandanten geklickt" ist in
            einem Rechteverwaltungswerkzeug der teuerste Fehler, den jemand
            machen kann: er faellt erst auf, wenn die Aenderung schon steht.

            Darum traegt die Kennzeichnung jetzt die Farbe des Mandanten als
            Flaeche. Der Punkt davor wiederholt sie in Form — Farbe allein
            unterscheidet fuer Menschen mit Farbsinnstoerung nichts, und
            COMLINE-Orange gegen DEV-Gruen ist genau die Paarung, die dabei
            zusammenfaellt.

            Die Flaeche kommt aus `--umgebung-flaeche`, das je Mandant gesetzt
            ist — nicht aus `--brand-strong`. Die Markenfarbe folgt dem Theme,
            und DEV setzte keines: die Kennzeichnung erschien dort in
            COMLINE-Orange und verdeckte damit genau den Unterschied, den sie
            zeigen soll. Alle drei Flaechen liegen ueber 7:1 gegen ihren Text.
          */}
          {/* Die Mandantenmarke entfaellt, wo die Wortmarke sie schon traegt.

              Bei HOWOGE stand der Name am 03.09.2026 zweimal nebeneinander:
              einmal im Logo, einmal als Marke daneben. Ihr Zweck ist genau
              dieser eine — zu sagen, in welchem Mandanten man handelt — und
              die Wortmarke tut das deutlicher, groesser und in der Farbe des
              Hauses.

              Fuer COMLINE und DEV bleibt sie, denn dort steht oben links das
              gemeinsame Zeichen und kein Name. Der Grund dafuer hat nichts
              von seinem Gewicht verloren: „im falschen Mandanten geklickt“
              ist in einem Rechtewerkzeug der teuerste Fehler, weil er erst
              auffaellt, wenn die Aenderung steht. */}
          {!eigeneWortmarke && (
            <span className="kopf-mandant" title={'Aktueller Mandant: ' + mandantName(p.marke)}
                  style={stil(
              'display:inline-flex;align-items:center;gap:6px;' +
              'font-size:var(--grad-xs);font-weight:700;letter-spacing:.08em;' +
              'padding:3px 9px;border-radius:999px;white-space:nowrap;' +
              'background:var(--umgebung-flaeche);color:var(--umgebung-text)',
            )}>
              <span aria-hidden="true" style={stil(
                'width:5px;height:5px;border-radius:999px;background:currentColor;' +
                (p.marke === 'dev' ? 'box-shadow:0 0 0 2px color-mix(in oklab, currentColor 45%, transparent)' : ''),
              )} />
              {mandantName(p.marke)}
            </span>
          )}
        </a>

        <button onClick={() => setzePalette(true)} aria-label="Globale Suche öffnen"
                className="kopf-suche" style={stil(
          'align-items:center;gap:9px;height:34px;margin-left:8px;padding:0 10px;' +
          'background:var(--surface-sunken);border:1px solid var(--border-subtle);' +
          'border-radius:var(--r-md);color:var(--text-tertiary);font-size:13px;cursor:pointer',
        )}>
          <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3" />
          </svg>
          <span style={stil('flex:1;text-align:left;overflow:hidden;text-overflow:ellipsis;white-space:nowrap')}>
            {p.profil.module.personen !== false
              ? 'Rolle, Person, Asset suchen…'
              : 'Rolle, Asset, Gruppe suchen…'}
          </span>
        </button>

        <div className="kopf-rechts" style={stil(
          'margin-left:auto;display:flex;align-items:center;gap:4px;flex:0 0 auto',
        )}>
          <div className="kopf-voll" style={stil('align-items:center;gap:4px')}>
            {/* Das Auge ist am 01.09.2026 entfallen.
                Es schaltete den Vertraulichkeitsmodus: personenbezogene
                Angaben und Geldbetraege wurden unscharf gezeichnet. Der
                Kunde hat ihn abbestellt — „das wird nicht mehr verwendet".
                Und er traegt in dieser Instanz ohnehin wenig: HOWOGE fuehrt
                keine Personen, es gaebe nur Betraege zu verdecken.
                Mit ihm sind Zustand, Wirkung und die CSS-Regeln gegangen —
                ein Merkmal, das niemand mehr setzen kann, ist toter Code. */}

            {/* Der Rueckblick. Nur mit dem Recht dazu — und dann an der
                Stelle, an der alles steht, was auf die ganze Anwendung
                wirkt. */}
            {p.darfDas('stichtag.lesen')
              && <Stichtagswahl verlaufAb={p.verlaufAb ?? null} />}

            {/* Nur bei mehreren Mandanten. Eine Instanz mit festem Mandanten
                zeigt hier nichts — ein Umschalter mit genau einem Ziel sieht
                aus wie eine Wahl und ist keine. */}
            {MARKEN.length > 1 && (
              <Segmente
                beschriftung="Mandant"
                eintraege={MARKEN.map(([k, l]) => ({
                  schluessel: k, label: l, titel: 'Corporate Design ' + l,
                  aktiv: p.marke === k, setzen: () => p.setzeMarke(k),
                }))}
              />
            )}

            <Segmente
              beschriftung="Erscheinungsbild"
              eintraege={THEMES.map(([k, l, icon]) => ({
                schluessel: k, titel: 'Erscheinungsbild ' + l, icon,
                aktiv: p.thema === k, setzen: () => p.setzeThema(k),
              }))}
            />

            {/* Hier stand eine Reihe von Namenskuerzeln „gerade aktiv" — und sie
                kam aus `PRESENCE` in `entwurf/huelle.ts`, einer festen Liste
                erfundener Personen samt Seite und Uhrzeit. Dieselbe Klasse wie
                das erfundene Audit-Log und die erfundenen Tickets, nur an der
                sichtbarsten Stelle der Anwendung: oben rechts, dauerhaft.
                Entfallen am 14.08.2026 auf Wunsch. Wer wirklich angemeldet ist,
                steht unter `/aktive-nutzer`. */}
          </div>

          {/* Hier stand ein Zahnrad auf `/einstellungen` — ein Nachbau des
              Entwurfsabschnitts „G6 Einstellungen", der **keine einzige
              Schnittstelle** aufrief. Sein Speichern-Knopf sagte es selbst:
              „Der Schreibpfad zur API fehlt noch." Er arbeitete ausserdem auf
              den festen Personas aus `navigation.ts` (IAM Approver, IAM
              Editor, …), die es im Bestand seit dem Rollenumbau nicht mehr
              gibt, und fuehrte „Nutzerzugriffe" und „SBOM" als dauerhaft
              gesperrt — was aussah wie ein fehlendes Recht und keines war.

              Zwei Bedienflaechen fuer dieselbe Sache sind schlimmer als eine:
              die echte Rechteverwaltung steht unter „System" —
              Berechtigungsfeld (`/rechte`), Konten & Rollen (`/konten`) und
              Audit-Log (`/audit`), alle drei an der Schnittstelle. Entfallen
              am 20.08.2026 auf Wunsch. */}

          {/* Das Fragezeichen ist am 14.08.2026 auf Wunsch entfallen. Der
              Wegweiser selbst bleibt: die Tastenkuerzel `?` und `n` oeffnen
              ihn, und in der Seitenleiste steht „Wegweiser oeffnen". */}

          <button onClick={() => { setzeGlocke((g) => !g); setzeKonto(false); }}
                  aria-label="Benachrichtigungen" aria-expanded={glocke}
                  className="knopf-still"
                  style={stil('position:relative;' + knopf)}>
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7M10.3 20a2 2 0 0 0 3.4 0" />
            </svg>
            {!gelesen && ungelesen > 0 && (
              <span className="num" style={stil(
                'position:absolute;top:3px;right:3px;min-width:16px;height:16px;padding:0 4px;' +
                'border-radius:999px;background:var(--brand-primary);color:var(--text-on-brand);' +
                'font-size:12px;font-weight:600;line-height:16px;text-align:center',
              )}>{ungelesen}</span>
            )}
          </button>

          <button onClick={() => { setzeKonto((k) => !k); setzeGlocke(false); }}
                  aria-label="Konto-Menü" aria-expanded={konto}
                  className="knopf-still"
                  style={stil(
                    'display:flex;align-items:center;gap:8px;height:36px;padding:0 6px 0 4px;' +
                    'background:none;border:none;border-radius:999px;cursor:pointer',
                  )}>
            <span style={stil(
              'width:30px;height:30px;border-radius:999px;background:var(--brand-strong);' +
              'color:var(--text-on-brand);display:grid;place-items:center;font-size:12px;font-weight:600',
            )}>{initialen(name)}</span>
            <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                 style={stil('color:var(--text-tertiary)')}>
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        </div>
      </header>

      {glocke && <Glockenmenue stand={stand} gelesen={gelesen} onAlleGelesen={() => setzeGelesen(true)}
                               onSchliessen={() => setzeGlocke(false)} />}
      {konto && (
        <Kontomenue
          name={name} upn={upn} profil={p.profil}
          persona={p.persona} setzePersona={p.setzePersona}
          {...(p.konten ? { konten: p.konten } : {})}
          {...(p.gewaehltesKonto !== undefined ? { konto: p.gewaehltesKonto } : {})}
          {...(p.setzeGewaehltesKonto ? { setzeKonto: p.setzeGewaehltesKonto } : {})}
          marke={p.marke} setzeMarke={p.setzeMarke}
          thema={p.thema} setzeThema={p.setzeThema}
          onSchliessen={() => setzeKonto(false)}
        />
      )}
      {wegweiser && (
        <Wegweiser
          profil={p.profil}
          darfDas={p.darfDas}
          onSchliessen={() => setzeWegweiser(false)}
          onZurSuche={() => { setzeWegweiser(false); setzePalette(true); }}
        />
      )}
      {palette && (
        <Palette profil={p.profil} mandant={p.mandant} darfDas={p.darfDas}
                 onSchliessen={() => setzePalette(false)} />
      )}
    </>
  );
}

/* --------------------------------------------------------- Segmentgruppe */

/**
 * Der Rückblick — ein Tag der letzten 30, rein lesend.
 *
 * Steht nur dort, wo `stichtag.lesen` vorliegt: bei HOWOGE trägt das nur
 * `iam-admin`. Wer es nicht hat, sieht diesen Knopf nicht — nicht ausgegraut,
 * sondern gar nicht. Dieselbe Hausregel wie überall: „ein Knopf, der nichts
 * tun darf, verschwindet."
 *
 * Gewählt wird ein **Tag**, nicht ein Zeitpunkt. Was daraus wird — das Ende
 * dieses Tages in der Zeitzone des Browsers —, rechnet `Standrahmen.tsx`.
 */
function Stichtagswahl({ verlaufAb }: { verlaufAb: string | null }) {
  const stichtag = useStand();
  const setzen = useStandSetzen();
  const [offen, setzeOffen] = useState(false);
  const schale = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!offen) return;
    const draussen = (e: MouseEvent) => {
      if (!schale.current?.contains(e.target as Node)) setzeOffen(false);
    };
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') setzeOffen(false); };
    document.addEventListener('mousedown', draussen);
    document.addEventListener('keydown', taste);
    return () => {
      document.removeEventListener('mousedown', draussen);
      document.removeEventListener('keydown', taste);
    };
  }, [offen]);

  const waehlen = (tag: string) => { setzen(tag); setzeOffen(false); };

  /* Die Grenzen der Wahl.
   *
   * Frueh: dreissig Tage zurueck — oder der Tag, an dem der Verlauf beginnt,
   * wenn der spaeter liegt. Spaet: gestern. Liegt der Beginn auf heute, gibt
   * es noch keinen vollen Tag zum Zurueckschauen; dann steht statt der Wahl
   * ein Satz, der sagt, warum. */
  const frueh = verlaufAb && verlaufAb > tagVor(STICHTAG_TAGE) ? verlaufAb : tagVor(STICHTAG_TAGE);
  const spaet = tagVor(1);
  const nochNichts = frueh > spaet;
  const schnellwahl: [string, number][] = ([['gestern', 1], ['vor 7 Tagen', 7],
    ['vor 14 Tagen', 14], [`vor ${STICHTAG_TAGE} Tagen`, STICHTAG_TAGE]] as [string, number][])
    .filter(([, tage]) => tagVor(tage) >= frueh);

  return (
    <div ref={schale} style={stil('position:relative')}>
      <button onClick={() => setzeOffen((o) => !o)} aria-expanded={offen}
              aria-haspopup="dialog"
              title={stichtag
                ? `Rückblick auf den ${datum(stichtag.tag)} — zum Ändern öffnen`
                : 'Rückblick auf einen Stichtag der letzten 30 Tage'}
              style={stil(
        'display:flex;align-items:center;gap:6px;height:34px;padding:0 10px;cursor:pointer;'
        + 'border:1px solid ' + (stichtag ? 'var(--warning)' : 'var(--border-subtle)') + ';'
        + 'border-radius:var(--r-md);font-size:13px;'
        + (stichtag
          ? 'background:var(--warning-subtle);color:var(--warning);font-weight:600'
          : 'background:none;color:var(--text-secondary)'),
      )}>
        <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5M12 7v5l3 2" />
        </svg>
        {stichtag ? datum(stichtag.tag) : 'Rückblick'}
      </button>

      {offen && (
        <div role="dialog" aria-label="Rückblick auf einen Stichtag" style={stil(
          'position:absolute;top:40px;right:0;z-index:var(--z-menue);width:280px;'
          + 'padding:14px;text-align:left;background:var(--surface-card);'
          + 'border:1px solid var(--border-default);border-radius:var(--r-md);'
          + 'box-shadow:0 10px 28px rgb(0 0 0/.14)',
        )}>
          <p style={stil('margin:0 0 4px;font-size:14px;font-weight:600')}>
            Stand ansehen
          </p>
          <p style={stil(
            'margin:0 0 12px;font-size:12px;line-height:17px;color:var(--text-secondary)',
          )}>
            Die Anwendung zeigt den Bestand des gewählten Tages. Rein lesend —
            geändert wird nichts, und Ihre Rechte bleiben die von heute.
          </p>

          {nochNichts && (
            <p style={stil('margin:0 0 4px;font-size:13px;line-height:19px;'
              + 'color:var(--warning)')}>
              Der Verlauf beginnt heute. Den ersten Tag zum Zurückschauen gibt es
              ab morgen.
            </p>
          )}

          <label style={stil('display:block;font-size:12px;color:var(--text-secondary)'
            + (nochNichts ? ';opacity:.5' : ''))}>
            Tag
            <input type="date" value={stichtag?.tag ?? ''}
                   min={frueh} max={spaet} disabled={nochNichts}
                   onChange={(e) => { if (e.target.value) waehlen(e.target.value); }}
                   style={stil(
              'display:block;width:100%;height:36px;margin-top:4px;padding:0 8px;'
              + 'background:var(--surface-page);color:var(--text-primary);'
              + 'border:1px solid var(--border-subtle);border-radius:var(--r-md);font-size:13px',
            )} />
          </label>

          <div style={stil('display:flex;flex-wrap:wrap;gap:6px;margin-top:10px')}>
            {schnellwahl.map(([label, tage]) => (
              <button key={String(tage)} onClick={() => waehlen(tagVor(tage))}
                      style={stil(
                'height:28px;padding:0 10px;background:none;cursor:pointer;'
                + 'border:1px solid var(--border-subtle);border-radius:999px;'
                + 'color:var(--text-secondary);font-size:12px',
              )}>{label}</button>
            ))}
          </div>

          {stichtag && (
            <button onClick={() => { setzen(null); setzeOffen(false); }} style={stil(
              'display:block;width:100%;height:36px;margin-top:12px;cursor:pointer;'
              + 'background:var(--brand-strong);color:var(--text-on-brand);border:none;'
              + 'border-radius:var(--r-md);font-size:13px;font-weight:600',
            )}>
              Zurück zu heute
            </button>
          )}

          <p style={stil(
            'margin:12px 0 0;font-size:11px;line-height:16px;color:var(--text-tertiary)',
          )}>
            {verlaufAb
              ? `Der Verlauf beginnt am ${datum(verlaufAb)} und reicht ${STICHTAG_TAGE} Tage zurück. `
              : `Der Verlauf reicht ${STICHTAG_TAGE} Tage zurück. `}
            Ansichten, die keinen Verlauf führen — Cockpit, Konten, Freigaben,
            Audit-Log — sagen es, statt heutige Zahlen zu zeigen.
          </p>
        </div>
      )}
    </div>
  );
}

function Segmente({ beschriftung, eintraege }: {
  beschriftung: string;
  eintraege: { schluessel: string; label?: string; titel: string; icon?: string; aktiv: boolean; setzen: () => void }[];
}) {
  return (
    <div role="group" aria-label={beschriftung} style={stil(
      'display:flex;height:34px;border:1px solid var(--border-subtle);border-radius:var(--r-md);overflow:hidden',
    )}>
      {eintraege.map((e) => (
        <button key={e.schluessel} onClick={e.setzen} aria-pressed={e.aktiv}
                title={e.titel} aria-label={e.titel} style={stil(segStil(e.aktiv))}>
          {e.icon ? (
            <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
              <path d={symbol(e.icon)} />
            </svg>
          ) : e.label}
        </button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------- Glocke */

function Glockenmenue({ stand, gelesen, onAlleGelesen, onSchliessen }: {
  stand: Meldungsstand;
  gelesen: boolean;
  onAlleGelesen: () => void;
  onSchliessen: () => void;
}) {
  const huelle = useAussenklick(onSchliessen);
  /* „Alle anzeigen" verlaengert die Liste **an Ort und Stelle**.
     Vorher sprang der Verweis auf `/audit` — das Fenster schloss sich, die
     Seite wechselte, und das Ziel zeigte etwas anderes als das, worauf man
     geklickt hatte. Der Kunde hat am 24.08.2026 verlangt, dass sich die Liste
     ausklappt und das Fenster stehen bleibt. */
  const [lang, setzeLang] = useState(false);
  return (
    <div ref={huelle} role="dialog" aria-label="Benachrichtigungen" style={stil(
      'position:fixed;top:58px;right:56px;z-index:var(--z-menue);width:380px;max-width:calc(100vw - 24px);' +
      'background:var(--surface-raised);border:1px solid var(--border-subtle);' +
      'border-radius:var(--r-lg);box-shadow:var(--e2);animation:einblenden .15s;overflow:hidden',
    )}>
      <div style={stil(
        'display:flex;align-items:center;gap:12px;padding:12px 14px;border-bottom:1px solid var(--border-subtle)',
      )}>
        <span style={stil('font-size:14px;font-weight:600')}>Benachrichtigungen</span>
        <button onClick={onAlleGelesen} style={stil(
          'margin-left:auto;background:none;border:none;color:var(--brand-strong);font-size:12px;cursor:pointer',
        )}>Alle als gelesen markieren</button>
      </div>
      {/* Ohne Uebergang auf `max-height`: eine Hoehe zu animieren laesst den
          Browser bei jedem Bild neu umbrechen — und das an einem Behaelter,
          der zugleich scrollt. Der Detektor meldet es zu Recht. Die Liste
          waechst hier von drei auf drei Eintraege; ein Uebergang haette nichts
          zu zeigen. */}
      <div style={stil(
        'max-height:' + (lang ? '70vh' : '380px') + ';overflow-y:auto',
      )}>
        {stand.meldungen.map((m: Bestandsmeldung) => (
          <a key={m.href} href={m.href} onClick={onSchliessen} className="meldung" style={stil(
            'display:flex;gap:11px;padding:11px 14px;border-bottom:1px solid var(--border-subtle);' +
            'text-decoration:none;color:var(--text-primary)',
          )}>
            <span style={stil(
              'flex:0 0 6px;width:6px;height:6px;margin-top:7px;border-radius:999px;background:' +
              (gelesen ? 'transparent' : 'var(--brand-primary)'),
            )} />
            <span style={stil(
              'flex:0 0 28px;width:28px;height:28px;display:grid;place-items:center;' +
              'border-radius:var(--r-sm);background:var(--surface-sunken);color:var(' + m.farbe + ')',
            )}>
              <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d={symbol(m.icon)} />
              </svg>
            </span>
            <span style={stil('flex:1;min-width:0')}>
              <span style={stil('display:block;font-size:13px;font-weight:' + (gelesen ? '400' : '600'))}>
                {m.titel}
              </span>
              <span style={stil('display:block;font-size:12px;color:var(--text-secondary);line-height:17px')}>
                {m.text}
              </span>
            </span>
          </a>
        ))}

        {stand.laedt && stand.meldungen.length === 0 && (
          <div aria-busy="true" style={stil(
            'padding:20px 14px;font-size:13px;color:var(--text-secondary)',
          )}>Wird geladen…</div>
        )}

        {stand.fehler && (
          <div style={stil(
            'padding:14px;font-size:13px;color:var(--text-primary);background:var(--danger-subtle)',
          )}>
            Mindestens ein Zähler konnte nicht geladen werden. Was hier steht, ist
            deshalb möglicherweise unvollständig.
          </div>
        )}

        {!stand.laedt && !stand.fehler && stand.meldungen.length === 0 && (
          <div style={stil('padding:22px 16px;text-align:center')}>
            <p style={stil('margin:0 0 4px;font-size:13px;font-weight:500')}>
              {stand.aus ? 'Für Sie werden keine Vorgänge geführt.' : 'Nichts offen.'}
            </p>
            <p style={stil('margin:0;font-size:12px;color:var(--text-secondary);line-height:17px')}>
              {stand.aus
                ? 'Die Module, die Vorgänge erzeugen, sind in dieser Instanz aus — oder '
                  + 'Ihnen fehlt das Recht, sie zu sehen.'
                : 'Keine offene Freigabe, kein wartender Zugang, keine fällige '
                  + 'Rezertifizierung.'}
            </p>
          </div>
        )}
      </div>
      <button
        onClick={() => setzeLang((o) => !o)}
        aria-expanded={lang}
        style={stil(
          'display:block;width:100%;padding:10px;text-align:center;font-size:13px;' +
          'background:none;border:none;border-top:1px solid var(--border-subtle);' +
          'color:var(--brand-strong);cursor:pointer',
        )}>
        {lang ? 'Weniger anzeigen' : 'Alle anzeigen'}
      </button>
    </div>
  );
}

/* --------------------------------------------------------- Konto-Menue */

function Kontomenue(p: {
  name: string; upn: string;
  profil: Profil;
  persona: string; setzePersona: (p: string) => void;
  /** Konten des Entwicklungsbetriebs; leer, sobald die Anmeldung entscheidet. */
  konten?: { kennung: string; name: string; anwendungsrolle: string; oeCode: string }[];
  konto?: string; setzeKonto?: (k: string) => void;
  marke: string; setzeMarke: (m: string) => void;
  thema: string; setzeThema: (t: string) => void;
  onSchliessen: () => void;
}) {
  const huelle = useAussenklick(p.onSchliessen);
  return (
    <div ref={huelle} role="dialog" aria-label="Konto-Menü" style={stil(
      'position:fixed;top:58px;right:12px;z-index:var(--z-menue);width:300px;background:var(--surface-raised);' +
      'border:1px solid var(--border-subtle);border-radius:var(--r-lg);box-shadow:var(--e2);' +
      'padding:14px;animation:einblenden .15s',
    )}>
      <div style={stil(
        'display:flex;align-items:center;gap:12px;padding-bottom:12px;border-bottom:1px solid var(--border-subtle)',
      )}>
        <span style={stil(
          'width:40px;height:40px;border-radius:999px;background:var(--brand-strong);' +
          'color:var(--text-on-brand);display:grid;place-items:center;font-weight:600',
        )}>{initialen(p.name)}</span>
        <div style={stil('min-width:0')}>
          {p.name || p.upn ? (
            <>
              <div className="pii" style={stil('font-size:14px;font-weight:600')}>{p.name}</div>
              <div className="pii" style={stil('font-size:12px;color:var(--text-tertiary);overflow:hidden;text-overflow:ellipsis')}>
                {p.upn}
              </div>
            </>
          ) : (
            /* Kein Name statt eines erfundenen. Bis zum 27.08.2026 stand hier
               bei jedem Ausfall von `/api/ich` „Lennart Ahrens". */
            <div style={stil('font-size:13px;color:var(--text-secondary)')}>
              Angemeldetes Konto nicht abrufbar
            </div>
          )}
        </div>
      </div>

      {/* Auf schmalen Fenstern wandern Mandant, Erscheinungsbild und
          Vertraulichkeit aus der Kopfzeile hierher. */}
      <div className="konto-extras" style={stil(
        'padding:10px 0;border-bottom:1px solid var(--border-subtle);flex-direction:column;gap:9px',
      )}>
        {MARKEN.length > 1 && (
          <div style={stil('display:flex;align-items:center;gap:8px')}>
            <span style={stil('flex:1;font-size:12px;color:var(--text-secondary)')}>Mandant</span>
            <Segmente beschriftung="Mandant" eintraege={MARKEN.map(([k, l]) => ({
              schluessel: k, label: l, titel: 'Corporate Design ' + l,
              aktiv: p.marke === k, setzen: () => p.setzeMarke(k),
            }))} />
          </div>
        )}
        <div style={stil('display:flex;align-items:center;gap:8px')}>
          <span style={stil('flex:1;font-size:12px;color:var(--text-secondary)')}>Erscheinungsbild</span>
          <Segmente beschriftung="Erscheinungsbild" eintraege={THEMES.map(([k, l, icon]) => ({
            schluessel: k, titel: 'Erscheinungsbild ' + l, icon,
            aktiv: p.thema === k, setzen: () => p.setzeThema(k),
          }))} />
        </div>
        <a href="/aktive-nutzer" onClick={p.onSchliessen}
           style={stil('font-size:12px;color:var(--text-secondary)')}>Aktive Nutzer ansehen</a>
      </div>

      {/* Kontowechsel statt Personawechsel.

          Die Persona war eine Angabe des Browsers und entschied frueher ueber
          die eigenen Rechte. Sobald die Schnittstelle Konten fuehrt, wird hier
          das **Konto** gewechselt — die Rechte kommen dann von dort. Der
          Personablock bleibt als Rueckfall, solange es keine Konten gibt.

          Sichtbar nur im Entwicklungsbetrieb: in einer ausgelieferten Anwendung
          sucht sich niemand sein Konto aus. */}
      {p.konten && p.konten.length > 0 ? (
        <div style={stil('padding:10px 0;border-bottom:1px solid var(--border-subtle)')}>
          <div style={stil(
            'font-size:12px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;' +
            'color:var(--text-tertiary);margin-bottom:6px',
          )}>Konto wechseln (Entwicklung)</div>
          <label style={stil('display:flex;align-items:center;gap:8px')}>
            <span className="nur-vorlesen">Konto</span>
            <select
              value={p.konto ?? ''}
              onChange={(ev) => p.setzeKonto?.(ev.target.value)}
              aria-label="Konto des Entwicklungsbetriebs wählen"
              style={stil(
                'flex:1;min-width:0;min-height:36px;padding:0 8px;background:var(--surface-card);' +
                'border:1px solid var(--border-eingabe);border-radius:var(--r-md);font-size:13px',
              )}
            >
              <option value="">Oberstes Konto (alle Rechte)</option>
              {p.konten.map((k) => (
                <option key={k.kennung} value={k.kennung}>
                  {k.name} · {k.oeCode} · {k.anwendungsrolle}
                </option>
              ))}
            </select>
          </label>
          <p style={stil('margin:6px 0 0;font-size:12px;line-height:17px;color:var(--text-secondary)')}>
            Die Rechte kommen aus der Schnittstelle. Ein Wechsel zeigt die
            Anwendung so, wie dieses Konto sie sieht.
          </p>
        </div>
      ) : DEMO ? (
        /* Nur im Demobau. Dort waehlt `demo/datenquelle.ts` je Persona ein
           **Konto** des Bestands; die Rechte kommen anschliessend aus
           `/api/ich` wie ueberall sonst. In der Anwendung gibt es diesen
           Umschalter seit dem 27.08.2026 nicht mehr — er stellte dort einen
           Wert im Browserspeicher, aus dem die Anwendung ihre Auskunft
           ueber die eigenen Rechte ableitete. */
        <div style={stil('padding:10px 0;border-bottom:1px solid var(--border-subtle)')}>
          <div style={stil(
            'font-size:12px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;' +
            'color:var(--text-tertiary);margin-bottom:6px',
          )}>Konto wechseln (Demo)</div>
          <div style={stil('display:flex;flex-direction:column;gap:3px')}>
            {DEMO_PERSONAS.map(([persona, hinweis]) => (
              <button key={persona} onClick={() => p.setzePersona(persona)}
                      aria-pressed={p.persona === persona} style={stil(
                'display:flex;align-items:center;gap:8px;padding:7px 10px;border-radius:var(--r-md);' +
                'border:none;cursor:pointer;font-size:13px;' +
                (p.persona === persona
                  ? 'background:var(--brand-primary-subtle);color:var(--brand-strong);font-weight:600'
                  : 'background:none;color:var(--text-primary)'),
              )}>
                <span style={stil('flex:1;text-align:left')}>{persona}</span>
                <span style={stil('font-size:12px;color:var(--text-tertiary)')}>{hinweis}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div style={stil('padding-top:8px;display:flex;flex-direction:column;gap:1px')}>
        {/* Auch das Konto-Menue haelt sich an das Mandantenprofil: „Meine
            Zugriffe“ zeigt auf eine Personenakte und darf bei einem Mandanten
            ohne Personenbezug nicht auftauchen. */}
        {KONTO_EINTRAEGE.filter(([, href]) => routeErlaubt(href, p.profil)).map(([label, href]) => (
          <a key={label} href={href} onClick={p.onSchliessen} className="menueintrag" style={stil(
            'display:block;padding:8px 10px;border-radius:var(--r-md);font-size:13px;' +
            'color:var(--text-primary);text-decoration:none',
          )}>{label}</a>
        ))}

        {/* Abmelden ist eine Handlung, kein Verweis — und die einzige im Menue,
            die den Rechner verlaesst. Deshalb abgesetzt und als Schaltflaeche. */}
        <span aria-hidden="true" style={stil(
          'display:block;height:1px;margin:5px 6px;background:var(--border-subtle)',
        )} />
        <button type="button" className="menueintrag"
                onClick={() => { p.onSchliessen(); void abmelden(); }}
                style={stil(
                  'display:block;width:100%;padding:8px 10px;border:none;background:none;' +
                  'border-radius:var(--r-md);font-size:13px;text-align:left;' +
                  'color:var(--text-primary);cursor:pointer',
                )}>
          Abmelden
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ Wegweiser */

function Wegweiser({ profil, darfDas, onSchliessen, onZurSuche }: {
  profil: Profil;
  /** Die echten Rechte aus `/api/ich`; das Schloss haengt an ihnen. */
  darfDas: (recht: string | null) => boolean;
  onSchliessen: () => void;
  onZurSuche: () => void;
}) {
  /* Fuer die Begruendung am Schloss: im Rueckblick fehlt nicht das Recht. */
  const stichtag = useStand();
  const [q, setzeQ] = useState('');
  const feld = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const drawer = useDrawer();

  useEffect(() => { feld.current?.focus(); }, []);
  useEffect(() => {
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') onSchliessen(); };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [onSchliessen]);

  const suche = q.trim().toLowerCase();
  /* Abgeschaltetes Modul heisst: die Kachel gehoert nicht her.
   *
   * Bis zum 25.08.2026 stand jede Kachel im Wegweiser — auch die eines
   * abgeschalteten Moduls, nur gesperrt und mit dem Titelzusatz „im aktuellen
   * Profil nicht verfuegbar". Bei HOWOGE traf das die ganze Gruppe „Personen":
   * eine Reihe grauer Kacheln fuer etwas, das es hier nicht gibt und nie geben
   * wird. Der Kunde hat sie beim Test am 24.08.2026 als unbrauchbare Eintraege
   * gemeldet.
   *
   * Ein **fehlendes Recht** bleibt dagegen sichtbar — mit Schloss. Der
   * Unterschied ist der zwischen „gibt es hier nicht" und „duerfen Sie nicht":
   * das eine ist der Zuschnitt der Instanz, das andere eine Rechtefrage, die
   * sich klaeren laesst. `App.tsx` zieht dieselbe Grenze fuer die Navigation. */
  const gruppen = KATALOG
    .map(([label, kacheln]) => [
      label,
      kacheln.filter(([titel, beschreibung, , , pfad]) =>
        zielErlaubt(pfad, profil) && (
          !suche ||
          titel.toLowerCase().includes(suche) ||
          beschreibung.toLowerCase().includes(suche))),
    ] as const)
    .filter(([, kacheln]) => kacheln.length > 0);

  const ziel = (pfad: string) => {
    onSchliessen();
    if (pfad.startsWith('form:')) drawer.oeffnen(pfad.slice(5));
    else navigate(pfad);
  };

  return (
    <div style={stil(
      'position:fixed;inset:0;z-index:var(--z-fenster);background:rgb(0 0 0/.46);display:flex;' +
      'align-items:center;justify-content:center;padding:32px 16px',
    )}>
      <button onClick={onSchliessen} aria-label="Wegweiser schließen"
              style={stil('position:absolute;inset:0;background:none;border:none;cursor:default')} />
      <div role="dialog" aria-modal="true" aria-label="Was möchten Sie anlegen oder tun?" style={stil(
        'position:relative;width:min(820px,100%);height:min(76vh,720px);min-width:min(460px,100%);' +
        'min-height:340px;max-width:100%;max-height:calc(100vh - 64px);resize:both;display:flex;' +
        'flex-direction:column;background:var(--surface-raised);border:1px solid var(--border-subtle);' +
        'border-radius:var(--r-lg);box-shadow:0 24px 64px rgb(0 0 0/.28);overflow:hidden;' +
        'animation:einblenden .18s cubic-bezier(.2,0,0,1)',
      )}>
        <span aria-hidden="true" title="Größe ändern" style={stil(
          'position:absolute;right:3px;bottom:3px;width:16px;height:16px;' +
          'border-right:2px solid var(--border-default);border-bottom:2px solid var(--border-default);' +
          'border-bottom-right-radius:4px;pointer-events:none',
        )} />
        <div style={stil('flex:0 0 auto;padding:20px 22px 16px;border-bottom:1px solid var(--border-subtle)')}>
          <div style={stil('display:flex;align-items:flex-start;gap:12px')}>
            <div style={stil('flex:1;min-width:0')}>
              <h2 style={stil('margin:0;font-size:19px;line-height:28px;font-weight:600')}>
                Was möchten Sie tun?
              </h2>
              <p style={stil('margin:2px 0 0;font-size:13px;line-height:20px;color:var(--text-secondary)')}>
                Wählen Sie eine Aufgabe — die Anwendung bringt Sie an die richtige Stelle.
                Sie müssen die Menüstruktur nicht kennen.
              </p>
            </div>
            <button onClick={onSchliessen} aria-label="Wegweiser schließen" className="knopf-still"
                    style={stil(
              'width:32px;height:32px;display:grid;place-items:center;background:none;border:none;' +
              'border-radius:var(--r-md);color:var(--text-secondary);cursor:pointer',
            )}>
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
          <div style={stil('position:relative;margin-top:14px')}>
            <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none"
                 stroke="var(--text-tertiary)" strokeWidth="2" strokeLinecap="round"
                 style={stil('position:absolute;left:11px;top:11px')}>
              <path d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3" />
            </svg>
            <input
              ref={feld} value={q} onChange={(e) => setzeQ(e.target.value)}
              placeholder="Aufgabe beschreiben — zum Beispiel „neuer Mitarbeiter“ oder „Laufwerk“"
              aria-label="Aufgabe suchen" autoComplete="off" spellCheck={false}
              style={stil(
                'width:100%;height:38px;padding:0 12px 0 34px;background:var(--surface-page);' +
                'border:1px solid var(--border-default);border-radius:var(--r-md);outline:none;font-size:14px',
              )}
            />
          </div>
        </div>

        <div style={stil('flex:1;overflow-y:auto;padding:8px 14px 16px')}>
          {gruppen.map(([label, kacheln]) => (
            <div key={label} style={stil('padding:10px 0 4px')}>
              <div style={stil(
                'font-size:12px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;' +
                'color:var(--text-tertiary);padding:0 8px 6px',
              )}>{label}</div>
              <div style={stil(
                'display:grid;grid-template-columns:repeat(auto-fill,minmax(min(100%,290px),1fr));' +
                'gap:2px;min-width:0',
              )}>
                {kacheln.map(([titel, beschreibung, icon, farbe, pfad, recht]) => {
                  /* Nur noch das Recht entscheidet ueber das Schloss — das
                     Modul hat die Kachel oben schon aussortiert. */
                  const gesperrt = !darfDas(recht);
                  /* Im Rueckblick fehlt nicht das Recht, sondern die
                     Gegenwart. „Dafuer fehlt Ihnen das Recht" waere dort
                     schlicht falsch — und eine falsche Begruendung schickt
                     Menschen zur Rechteverwaltung, die ihnen nicht hilft. */
                  const grund = stichtag
                    ? ' — im Rückblick nicht möglich'
                    : ' — dafür fehlt Ihnen das Recht';
                  return (
                    <button
                      key={titel}
                      onClick={() => !gesperrt && ziel(pfad)}
                      aria-disabled={gesperrt}
                      title={gesperrt ? titel + grund : titel}
                      className="wegweiser-kachel"
                      style={stil(
                        'display:flex;align-items:flex-start;gap:11px;width:100%;padding:11px 10px;' +
                        'background:none;border:none;border-radius:var(--r-md);text-align:left;' +
                        (gesperrt ? 'cursor:not-allowed;opacity:.55' : 'cursor:pointer'),
                      )}
                    >
                      <span style={stil(
                        'flex:0 0 34px;width:34px;height:34px;display:grid;place-items:center;' +
                        'border-radius:var(--r-md);background:var(--surface-sunken);color:var(' + farbe + ')',
                      )}>
                        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d={symbol(icon)} />
                        </svg>
                      </span>
                      <span style={stil('flex:1;min-width:0')}>
                        <span style={stil('display:flex;align-items:center;gap:7px')}>
                          <span style={stil('font-size:14px;font-weight:600')}>{titel}</span>
                          {gesperrt && (
                            <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                 style={stil('color:var(--text-tertiary)')}>
                              <path d="M6 11h12v10H6zM9 11V8a3 3 0 0 1 6 0v3" />
                            </svg>
                          )}
                        </span>
                        <span style={stil(
                          'display:block;font-size:12px;line-height:17px;color:var(--text-secondary);text-wrap:pretty',
                        )}>{beschreibung}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {!!suche && gruppen.length === 0 && (
            <div style={stil('padding:32px 16px;text-align:center')}>
              <p style={stil('margin:0 0 4px;font-size:14px;font-weight:500')}>
                Keine Aufgabe passt zu „{q}“
              </p>
              <p style={stil('margin:0;font-size:13px;line-height:20px;color:var(--text-secondary)')}>
                Suchen Sie direkt nach einer Rolle, Person, Anwendung oder Gruppe — oder wenden
                Sie sich an den Service Desk.
              </p>
            </div>
          )}
        </div>

        <div style={stil(
          'flex:0 0 auto;display:flex;align-items:center;gap:10px;padding:11px 22px;' +
          'border-top:1px solid var(--border-subtle);background:var(--surface-card)',
        )}>
          <span style={stil('font-size:12px;color:var(--text-tertiary)')}>
            Sie wissen schon, wonach Sie suchen?
          </span>
          <button onClick={onZurSuche} style={stil(
            'height:30px;padding:0 12px;background:var(--surface-card);' +
            'border:1px solid var(--border-default);border-radius:var(--r-md);font-size:12px;cursor:pointer',
          )}>Direkt suchen</button>
          <a href="/sitemap" onClick={onSchliessen} style={stil('margin-left:auto;font-size:12px')}>
            Alle Seiten im Überblick
          </a>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------ Command-Palette */

function Palette({ profil, mandant, darfDas, onSchliessen }: {
  profil: Profil; mandant: string;
  darfDas: (recht: string | null) => boolean;
  onSchliessen: () => void;
}) {
  const [q, setzeQ] = useState('');
  const feld = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => { feld.current?.focus(); }, []);
  useEffect(() => {
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') onSchliessen(); };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [onSchliessen]);

  const suche = q.trim().toLowerCase();
  const bestand = useGlobaleSuche(useEntprellt(q, 250), mandant, darfDas);

  const kacheln = KATALOG.flatMap(([bereich, k]) =>
    k
      .filter(([titel, beschreibung, , , pfad, recht]) =>
        offen(pfad, recht, profil, darfDas) && (!suche ||
          titel.toLowerCase().includes(suche) ||
          beschreibung.toLowerCase().includes(suche)))
      .map(([titel, beschreibung, , farbe, pfad]) => ({ bereich, titel, beschreibung, farbe, pfad })),
  ).slice(0, 40);

  return (
    <div style={stil(
      'position:fixed;inset:0;z-index:var(--z-fenster);background:rgb(0 0 0/.35);display:flex;' +
      'justify-content:center;padding:80px 16px',
    )}>
      <button onClick={onSchliessen} aria-label="Suche schließen"
              style={stil('position:absolute;inset:0;background:none;border:none;cursor:default')} />
      <div role="dialog" aria-modal="true" aria-label="Globale Suche" style={stil(
        'position:relative;width:640px;max-width:100%;height:fit-content;max-height:70vh;display:flex;' +
        'flex-direction:column;background:var(--surface-raised);border:1px solid var(--border-subtle);' +
        'border-radius:var(--r-lg);box-shadow:var(--e2);overflow:hidden;animation:einblenden .15s',
      )}>
        <div style={stil(
          'display:flex;align-items:center;gap:12px;padding:14px 18px;border-bottom:1px solid var(--border-subtle)',
        )}>
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke="var(--text-tertiary)" strokeWidth="2" strokeLinecap="round">
            <path d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3" />
          </svg>
          <input ref={feld} value={q} onChange={(e) => setzeQ(e.target.value)}
                 placeholder="Suchen oder Befehl eingeben…" aria-label="Suchbegriff"
                 autoComplete="off" spellCheck={false}
                 style={stil('flex:1;background:none;border:none;outline:none;font-size:16px')} />
          <button onClick={onSchliessen} style={stil(
            'font-family:var(--mono);font-size:12px;padding:3px 7px;background:none;' +
            'border:1px solid var(--border-subtle);border-radius:var(--r-sm);' +
            'color:var(--text-tertiary);cursor:pointer',
          )}>Esc</button>
        </div>
        <div style={stil('flex:1;overflow-y:auto;padding:6px')}>
          {/* Zuerst der Bestand, dann die Aufgaben.
              Wer `APM-AM-00` tippt, sucht die Rolle und nicht die Kachel, die
              zum Rollenkatalog fuehrt. */}
          {bestand.treffer.length > 0 && (
            <div style={stil('padding:4px 0')}>
              <div style={stil(
                'font-size:11px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;' +
                'color:var(--text-tertiary);padding:6px 10px 4px',
              )}>Im Bestand</div>
              {bestand.treffer.map((t) => (
                <button key={t.art + t.titel + t.pfad}
                        onClick={() => { onSchliessen(); navigate(t.pfad); }}
                        className="menueintrag"
                        style={stil(
                  'display:flex;align-items:center;gap:11px;width:100%;padding:9px 10px;background:none;' +
                  'border:none;border-radius:var(--r-md);cursor:pointer;text-align:left',
                )}>
                  <span style={stil(
                    'flex:0 0 8px;width:8px;height:8px;border-radius:2px;background:var(' + t.farbe + ')',
                  )} />
                  <span style={stil('flex:1;min-width:0')}>
                    <span style={stil(
                      'display:block;font-size:13px;font-weight:500;overflow:hidden;' +
                      'text-overflow:ellipsis;white-space:nowrap',
                    )}>{t.titel}</span>
                    <span style={stil(
                      'display:block;font-size:12px;color:var(--text-tertiary);overflow:hidden;' +
                      'text-overflow:ellipsis;white-space:nowrap',
                    )}>{t.unter}</span>
                  </span>
                  <span style={stil('font-size:12px;color:var(--text-tertiary);white-space:nowrap')}>
                    {t.art}
                  </span>
                </button>
              ))}
            </div>
          )}

          {bestand.laedt && bestand.treffer.length === 0 && (
            <div aria-busy="true" style={stil(
              'padding:14px 16px;font-size:13px;color:var(--text-secondary)',
            )}>Im Bestand wird gesucht…</div>
          )}

          {bestand.fehler && (
            <div style={stil(
              'margin:6px;padding:10px 12px;border-radius:var(--r-md);' +
              'background:var(--danger-subtle);font-size:13px;color:var(--text-primary)',
            )}>
              Der Bestand konnte nicht durchsucht werden. Die Aufgaben unten stehen trotzdem.
            </div>
          )}

          {kacheln.length > 0 && bestand.treffer.length > 0 && (
            <div style={stil(
              'font-size:11px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;' +
              'color:var(--text-tertiary);padding:10px 10px 4px;' +
              'border-top:1px solid var(--border-subtle);margin-top:4px',
            )}>Aufgaben</div>
          )}

          {kacheln.map((t) => (
            <button key={t.pfad + t.titel}
                    onClick={() => { onSchliessen(); navigate(t.pfad); }}
                    className="menueintrag"
                    style={stil(
              'display:flex;align-items:center;gap:11px;width:100%;padding:9px 10px;background:none;' +
              'border:none;border-radius:var(--r-md);cursor:pointer;text-align:left',
            )}>
              <span style={stil(
                'flex:0 0 8px;width:8px;height:8px;border-radius:2px;background:var(' + t.farbe + ')',
              )} />
              <span style={stil('flex:1;min-width:0')}>
                <span style={stil(
                  'display:block;font-size:13px;font-weight:500;overflow:hidden;' +
                  'text-overflow:ellipsis;white-space:nowrap',
                )}>{t.titel}</span>
                <span style={stil(
                  'display:block;font-size:12px;color:var(--text-tertiary);overflow:hidden;' +
                  'text-overflow:ellipsis;white-space:nowrap',
                )}>{t.beschreibung}</span>
              </span>
              <span style={stil('font-size:12px;color:var(--text-tertiary);white-space:nowrap')}>
                {t.bereich}
              </span>
            </button>
          ))}
          {kacheln.length === 0 && bestand.treffer.length === 0 && !bestand.laedt && (
            <div style={stil('padding:28px 16px;text-align:center')}>
              <p style={stil('margin:0 0 4px;font-size:14px;font-weight:500')}>
                {q.trim() ? 'Keine Ergebnisse für „' + q + '“' : 'Wonach suchen Sie?'}
              </p>
              {/* Der Leertext verspricht nur, was die Suche kann.
                  Bis zum 25.08.2026 riet er zu „Rollenschluessel, Anwendungsname
                  oder Group ID" — und keines davon fand sie, weil sie den
                  Bestand gar nicht fragte. */}
              <p style={stil('margin:0;font-size:13px;color:var(--text-secondary)')}>
                {q.trim().length === 1
                  ? 'Ab zwei Zeichen wird der Bestand durchsucht.'
                  : 'Gesucht wird in Rollen, Anwendungen, technischen Gruppen und '
                    + 'einzelnen Berechtigungen — dazu in allen Aufgaben dieser Anwendung.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- Helfer */

/** Schliesst beim Klick ausserhalb und bei Escape. */
function useAussenklick(onSchliessen: () => void) {
  const huelle = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const draussen = (e: MouseEvent) => {
      if (!huelle.current?.contains(e.target as Node)) onSchliessen();
    };
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') onSchliessen(); };
    // Erst im naechsten Takt lauschen, sonst schliesst der oeffnende Klick sofort wieder.
    const uhr = window.setTimeout(() => document.addEventListener('mousedown', draussen), 0);
    document.addEventListener('keydown', taste);
    return () => {
      clearTimeout(uhr);
      document.removeEventListener('mousedown', draussen);
      document.removeEventListener('keydown', taste);
    };
  }, [onSchliessen]);
  return huelle;
}

/**
 * Ist ein Ziel erreichbar? Zwei Bedingungen, beide aus dem Entwurf: das Modul
 * muss im Mandantenprofil an sein, und die Persona muss das Recht haben.
 */
/**
 * Steht dieses Ziel dem Angemeldeten offen?
 *
 * Zwei Fragen, und sie sind verschieden: `zielErlaubt` prueft den
 * **Mandantenzuschnitt** — was ein Mandant nicht fuehrt, gibt es fuer
 * niemanden. `darfDas` prueft das **Recht** aus `/api/ich`.
 *
 * Bis zum 27.08.2026 stand hier `darf([persona], recht)` gegen die Tabelle
 * `RECHTE` im Quelltext, ausgewaehlt ueber den Browserspeicher. Die
 * Suchpalette bot damit an, was eine erfundene Rolle durfte.
 */
function offen(
  ziel: string,
  recht: string | null,
  profil: Profil,
  darfDas: (recht: string | null) => boolean,
): boolean {
  return zielErlaubt(ziel, profil) && darfDas(recht);
}

